import React from "react";
import {
  Box,
  Typography,
  CardContent,
  Divider,
  LinearProgress,
} from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { AuditLogStatsDto } from "../../../shared/dtos/auditLog/AuditLogStatsDto";

const getActionColor = (
  actionType: string,
): "success" | "info" | "error" | "secondary" | "primary" => {
  switch (actionType?.toUpperCase()) {
    case "CREATE":
    case "INSERT":
      return "success";
    case "UPDATE":
    case "MODIFY":
      return "info";
    case "DELETE":
    case "REMOVE":
      return "error";
    case "LOGIN":
    case "AUTH":
      return "secondary";
    default:
      return "primary";
  }
};

const formatModuleName = (name: string): string => {
  if (!name) return "";
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

interface AuditLogWidgetProps {
  data: AuditLogStatsDto;
}

const AuditLogWidget: React.FC<AuditLogWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("AUDIT_LOG");
  const Icon = meta.icon;

  const actions = data.actionBreakdown || [];
  const topModules = data.topActiveModules || [];
  const safeTotal = data.totalActionsToday || 1;

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Actions Today:{" "}
              <Box component="b" sx={{ fontWeight: "bold" }}>
                {data.totalActionsToday}
              </Box>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 1 }}
          >
            Action Breakdown
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {actions.length > 0 ? (
              actions.map((action, index) => {
                const color = getActionColor(action.actionType);
                return (
                  <Box key={index}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {action.actionType.toLowerCase()}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {action.count}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(action.count / safeTotal) * 100}
                      color={color}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                );
              })
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.disabled", fontStyle: "italic" }}
              >
                No actions recorded today.
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 1 }}
          >
            Top Active Modules
          </Typography>
          {topModules.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
              }}
            >
              {topModules.map((mod, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    title={formatModuleName(mod.entityType)}
                  >
                    {formatModuleName(mod.entityType)}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    {mod.count}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      hits
                    </Typography>
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "text.disabled", fontStyle: "italic" }}
            >
              No module activity data.
            </Typography>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default AuditLogWidget;
